import { toast } from "react-toastify";

export function toastHandler(
  status = false,
  message,
  navigate = () => {},
  navigatePath = "",
  locationState
) {
  if (navigatePath) {
    navigate(navigatePath, { state: locationState });
  }

  const toastOptions = {
    autoClose: status ? 4000 : 3000,
  };

  if (status) {
    toast.success(message, toastOptions);
  } else {
    toast.error(message, toastOptions);
  }
}

// Format an ISO-ish date string (e.g. "2026-07-14") to "July 14, 2026".
// Returns the raw value if it can't be parsed.
export function formatFriendlyDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// Whole days left until an ISO-ish date string. Negative once the date passed.
// Returns null when the value is missing or unparseable.
export function daysUntil(value) {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

// Format a money value coming from the API (string or number) as "$100.00".
export function formatPrice(value) {
  const amount = Number(value);
  return `$${(isNaN(amount) ? 0 : amount).toFixed(2)}`;
}

// Display label for a Stripe card brand, e.g. "visa" -> "VISA".
export function getCardBrandLabel(brand) {
  if (!brand) return "CARD";
  const map = {
    visa: "VISA",
    mastercard: "Master Card",
    amex: "AMEX",
    discover: "Discover",
  };
  return map[brand.toLowerCase()] || brand.toUpperCase();
}

// Brand colour used for the card label.
export function getCardBrandColor(brand) {
  const colors = {
    visa: "#1A1F71",
    mastercard: "#EB001B",
    amex: "#006FCF",
    discover: "#FF6000",
  };
  return colors[brand?.toLowerCase()] || "#004D26";
}

// Flatten a saved card from /admin/stripe/cards, which may arrive either
// flat or nested under `card` depending on how Stripe serialised it.
export function normalizeCard(card) {
  return {
    id: card?.id || card?.payment_method_id || "",
    brand: card?.brand || card?.card?.brand || "",
    last4: card?.last4 || card?.card?.last4 || "",
    expMonth: card?.exp_month || card?.card?.exp_month || null,
    expYear: card?.exp_year || card?.card?.exp_year || null,
    funding: card?.funding || card?.card?.funding || "",
    country: card?.country || card?.card?.country || "",
    createdAt: card?.created_at || null,
    isDefault: Boolean(card?.is_default || card?.default),
  };
}

// Masked card number for display, e.g. "•••• •••• •••• 4242".
export function maskedCardNumber(last4) {
  return `•••• •••• •••• ${last4 || "••••"}`;
}

// "12/2027" from a Stripe expiry month/year pair.
export function formatCardExpiry(month, year) {
  if (!month || !year) return "--/----";
  return `${String(month).padStart(2, "0")}/${year}`;
}

// The cards API returns "Y-m-d H:i:s", not ISO-8601, so parse the parts
// explicitly rather than relying on Date to guess the format.
export function formatApiDateTime(value) {
  if (!value) return "";
  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/
  );
  if (!match) return formatFriendlyDate(value);
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// A purchased quest type can be renewed this many days before it expires.
export const RENEWAL_WINDOW_DAYS = 30;

// True once an entitlement is close enough to expiry that renewing makes
// sense. Before that it simply reads as purchased.
export function isRenewable(expiresAt) {
  const remaining = daysUntil(expiresAt);
  return remaining !== null && remaining <= RENEWAL_WINDOW_DAYS;
}

// acquired_via value for quest types granted by Organisation Free Access.
export const FREE_ACCESS = "free_access";

// True for a quest type currently granted through Organisation Free Access.
// Only this entitlement gets free-access messaging; purchased or default
// quest types keep their own behaviour, and an expired grant is not free.
export function isFreeAccessGrant(questType) {
  return (
    questType?.acquired_via === FREE_ACCESS &&
    Boolean(questType?.owned) &&
    !questType?.is_expired
  );
}

// When the organisation's free access ends, read from the marketplace
// response itself (the latest active free-access expires_at). Null when no
// free-access grant is active.
export function getFreeAccessUntil(bundles) {
  let latest = null;
  (bundles || []).forEach((bundle) => {
    (bundle?.quest_types || []).forEach((questType) => {
      if (!isFreeAccessGrant(questType) || !questType.expires_at) return;
      if (!latest || new Date(questType.expires_at) > new Date(latest)) {
        latest = questType.expires_at;
      }
    });
  });
  return latest;
}

// Calendar date of an API timestamp, read in UTC. Free-access expiry is sent
// as end of day UTC ("2026-12-16T23:59:59Z"); formatting that in local time
// shows the next day anywhere east of UTC (December 17 in India).
export function formatApiDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
